import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import Decimal from 'decimal.js';
import {WizardWidgetComponent} from '../wizard-widget/wizard-widget.component';
import {ownershipValidator} from '../../../_global/validators/custom-validators.directive';
import * as _ from 'lodash';
import {DataHandlerService} from '../../../services/data-handler.service';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {NgIf, NgFor} from '@angular/common';
import {debounceTime, take} from 'rxjs/operators';
import {MatTooltip} from '@angular/material/tooltip';

@Component({
  selector: 'app-ownership-allocation',
  templateUrl: 'ownership-allocation.component.html',
  standalone: true,
  imports: [NgIf, FormsModule, ReactiveFormsModule, NgFor, MatFormFieldModule, MatFormFieldModule, MatInputModule, MatInputModule, MatButtonModule, MatButtonModule, MatIconModule, MatTooltip]
})
export class OwnershipAllocationComponent extends WizardWidgetComponent implements OnInit {
  @Input() public personList: any;
  @Input() public companyList: any;
  @Input() public trustList: any;
  @Input() public formGroup: any;
  @Input() public formArray: any;
  @Input() public allocationType: any;
  @Input() public isSMSF = false;
  @Input() public isFurtherAdvance = false;
  @Input() public forceDisplay = false;
  @Output() public ownershipUpdated = new EventEmitter<any>();
  public productCategory: string;

  constructor(
    private formBuilder: UntypedFormBuilder,
    public dataHandlerService: DataHandlerService
  ) {
    super(dataHandlerService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initParties(this.formGroup);

    this.dataHandlerService.getProductCategory()
      .pipe(take(1))
      .subscribe((productCategory: string)=> {
        this.productCategory = productCategory;
      })
  }

  confirmReducedOwnership(formGroup) {
    formGroup.controls.parties.clearValidators();
    formGroup.controls.parties.updateValueAndValidity();
  }

  initParties(fg: UntypedFormGroup) {
    let initOwnershipValue = 100;

    const filteredPersons = this.personList.value.filter(c => !this.isSMSF);
    const filteredCompanies = this.companyList.value.filter(c => c.trusteeOf === null || c.trusteeOf === undefined);
    const filteredTrusts = this.trustList.value.filter(c => (c.trustType !== 'security-trust' && this.isSMSF) || !this.isSMSF);

    const totalLength = _.size([
      ...filteredPersons,
      ...filteredCompanies,
      ...filteredTrusts,
    ]);

    if (totalLength > 1) {
      initOwnershipValue = Number(new Decimal(100).div(totalLength).toFixed(4));
    }

    const tempPartiesValue = fg.value.parties;
    const partiesFormControl = <UntypedFormArray>fg.get('parties');

    while (partiesFormControl.length) {
      partiesFormControl.removeAt(0);
    }

    this.trustList.value.forEach((borrower: any, idx) => {
      if (filteredTrusts.includes(borrower)) {
        let borrowerIndex: number;
        let ownershipValue: number;

        if (borrower.partyId) {
          borrowerIndex = _.findIndex(tempPartiesValue, {id: borrower.partyId});
        }

        if (borrowerIndex < 0) {
          borrowerIndex = _.findIndex(tempPartiesValue, {name: borrower.trustName});
        }

        ownershipValue = (tempPartiesValue[borrowerIndex] && tempPartiesValue[borrowerIndex].ownership) ? tempPartiesValue[borrowerIndex].ownership : initOwnershipValue;

        partiesFormControl.push(this.formBuilder.group({
          name: new UntypedFormControl(borrower.trustName || `Trust ${idx + 1}`),
          ownership: new UntypedFormControl(ownershipValue, [Validators.required]),
          partyType: new UntypedFormControl('trust'),
          index: new UntypedFormControl(idx),
        }));
      }
    });

    this.companyList.value.forEach((borrower: any, idx) => {
      if (filteredCompanies.includes(borrower)) {
        let borrowerIndex: number;
        let ownershipValue: number;

        if (borrower.partyId) {
          borrowerIndex = _.findIndex(tempPartiesValue, {id: borrower.partyId});
        }

        if (borrowerIndex < 0) {
          borrowerIndex = _.findIndex(tempPartiesValue, {name: borrower.companyName});
        }

        ownershipValue = (tempPartiesValue[borrowerIndex] && tempPartiesValue[borrowerIndex].ownership) ? tempPartiesValue[borrowerIndex].ownership : initOwnershipValue;

        partiesFormControl.push(this.formBuilder.group({
          name: new UntypedFormControl(borrower.companyName || `Company ${idx + 1}`),
          ownership: new UntypedFormControl(ownershipValue, [Validators.required]),
          partyType: new UntypedFormControl('company'),
          index: new UntypedFormControl(idx),
        }));
      }
    });

    this.personList.value.forEach((borrower: any, idx) => {
      if (filteredPersons.includes(borrower)) {
        let borrowerIndex: number;
        let ownershipValue: number;

        if (borrower.partyId) {
          borrowerIndex = _.findIndex(tempPartiesValue, {id: borrower.partyId});
        }

        if (borrowerIndex < 0) {
          borrowerIndex = _.findIndex(tempPartiesValue, {name: `${borrower.firstName || ''} ${borrower.lastName || ''}`});
        }

        let name = `${borrower.firstName || ''} ${borrower.lastName || ''}`;
        if (!name.trim()) {
          name = `Person ${idx + 1}`;
        }

        ownershipValue = (tempPartiesValue[borrowerIndex] && tempPartiesValue[borrowerIndex].ownership) ? tempPartiesValue[borrowerIndex].ownership : initOwnershipValue;

        const personOwnershipFormGroup: any = this.formBuilder.group({
          name: new UntypedFormControl(name),
          ownership: new UntypedFormControl(ownershipValue, [Validators.required]),
          partyType: new UntypedFormControl('person'),
          index: new UntypedFormControl(idx),
          nonApplicant: new UntypedFormControl((!!borrower.nonApplicant), [Validators.required]),
        });

        if (this.allocationType && this.allocationType === 'assets') {
          personOwnershipFormGroup.get('ownership').valueChanges
            .pipe(debounceTime(500))
            .subscribe((value: any) => {
          });
        }

        partiesFormControl.push(personOwnershipFormGroup);
      }
    });

    partiesFormControl.setValidators(ownershipValidator());
  }
}
